import { DIFF_DELETE, DIFF_INSERT } from 'diff-match-patch';

import type { VisualDiffSegment } from './types';
import { DELETE_PLACEHOLDER, RANGE_END, RANGE_START } from '../layered-diffs/constants';
import { splitTextByRangeMarkers } from '../layered-diffs/diff-utils';
import type { DiffSegment } from '../layered-diffs/types';
import { diffWordsSemantic } from '../layered-diffs/word-diff';

type SegmentOrigin = 'equal' | 'oldInsert' | 'oldDelete';

type TrackedSegment = {
  origin: SegmentOrigin;
  text: string;
};

/**
 * Creates a visual diff that tracks both original diff operations and new changes.
 *
 * Takes an existing diff and a final text, then computes a visual representation
 * that shows:
 * - Original equal text (no flags)
 * - Original insertions still present (oldInsert)
 * - Original deletions (oldDelete)
 * - New insertions (newInsert)
 * - New deletions of original text (newDelete)
 * - Original insertions that were deleted (oldInsert + newDelete)
 *
 * @param existingDiff - The original diff with insert/delete/equal operations
 * @param finalText - The target text (may contain RANGE_START/RANGE_END markers)
 * @returns Visual diff segments with attribution flags
 */
export function createVisualDiff(
  existingDiff: DiffSegment[],
  finalText: string
): VisualDiffSegment[] {
  // Step 1: Build tracked segments from existing diff
  const trackedSegments: TrackedSegment[] = [];
  for (const change of existingDiff) {
    if (change.op === 'equal') {
      trackedSegments.push({ origin: 'equal', text: change.text });
    } else if (change.op === 'insert') {
      trackedSegments.push({ origin: 'oldInsert', text: change.text });
    } else {
      trackedSegments.push({ origin: 'oldDelete', text: change.text });
    }
  }

  /**
   * Step 2: Build visible text with placeholders for deletions.
   * Also build a position map to track origin of each character in visible text.
   */
  let visibleTextWithPlaceholders = '';
  const positionToOrigin: SegmentOrigin[] = [];
  const deletedTexts: string[] = [];

  for (const segment of trackedSegments) {
    if (segment.origin === 'oldDelete') {
      visibleTextWithPlaceholders += DELETE_PLACEHOLDER;
      positionToOrigin.push('oldDelete');
      deletedTexts.push(segment.text);
    } else {
      visibleTextWithPlaceholders += segment.text;
      for (let i = 0; i < segment.text.length; i++) {
        positionToOrigin.push(segment.origin);
      }
    }
  }

  // Step 3: Compute word-level diff between visible text (with placeholders) and final text
  const rawDiffs = diffWordsSemantic(visibleTextWithPlaceholders, finalText);

  // Step 4: Process the diff result
  const result: VisualDiffSegment[] = [];
  let visiblePos = 0;
  let deletedIndex = 0;

  for (const [op, text] of rawDiffs) {
    let remaining = text;
    let remainingStartPos = visiblePos;

    while (remaining.length > 0) {
      const placeholderIndex = remaining.indexOf(DELETE_PLACEHOLDER);

      if (placeholderIndex === -1) {
        // No placeholder in remaining text - process normally
        if (op === DIFF_INSERT) {
          // New insertion - split by range markers
          const parts = splitTextByRangeMarkers(remaining);
          for (const part of parts) {
            if (part.length > 0) {
              result.push({ newInsert: true, text: part });
            }
          }
        } else if (op === DIFF_DELETE) {
          // Deletion from visible text - check origin of each character
          processVisibleTextDeletion(remaining, remainingStartPos, positionToOrigin, result);
          visiblePos += remaining.length;
        } else {
          // EQUAL - text stays the same, check origin
          processVisibleTextEqual(remaining, remainingStartPos, positionToOrigin, result);
          visiblePos += remaining.length;
        }
        break;
      }

      // Handle text before the placeholder
      if (placeholderIndex > 0) {
        const beforePlaceholder = remaining.slice(0, placeholderIndex);

        if (op === DIFF_INSERT) {
          const parts = splitTextByRangeMarkers(beforePlaceholder);
          for (const part of parts) {
            if (part.length > 0) {
              result.push({ newInsert: true, text: part });
            }
          }
        } else if (op === DIFF_DELETE) {
          processVisibleTextDeletion(
            beforePlaceholder,
            remainingStartPos,
            positionToOrigin,
            result
          );
          visiblePos += beforePlaceholder.length;
          remainingStartPos += beforePlaceholder.length;
        } else {
          processVisibleTextEqual(beforePlaceholder, remainingStartPos, positionToOrigin, result);
          visiblePos += beforePlaceholder.length;
          remainingStartPos += beforePlaceholder.length;
        }
      }

      // Process the placeholder (represents old deleted text)
      const deletedText = deletedTexts[deletedIndex++];
      if (deletedText) {
        /**
         * Old deleted text is always marked as oldDelete regardless of the new diff operation.
         * A placeholder in EQUAL means it stayed deleted; in DELETE, it was "deleted again".
         * Since it is already deleted, we just mark it as oldDelete.
         */
        result.push({ oldDelete: true, text: deletedText });
      }

      // Move past the placeholder in visible position tracking
      visiblePos += 1;
      remainingStartPos += 1;

      // Continue with text after the placeholder
      remaining = remaining.slice(placeholderIndex + 1);
    }
  }

  // Step 5: Merge adjacent segments with same flags
  return mergeAdjacentSegments(result);
}

/**
 * Processes text from the visible string that is being kept (EQUAL in diff).
 * Determines if it's original equal text or was an old insertion.
 */
function processVisibleTextEqual(
  text: string,
  startPos: number,
  positionToOrigin: SegmentOrigin[],
  result: VisualDiffSegment[]
): void {
  let currentOrigin: SegmentOrigin | null = null;
  let currentText = '';

  for (let i = 0; i < text.length; i++) {
    const origin = positionToOrigin[startPos + i];

    if (origin !== currentOrigin) {
      if (currentText.length > 0 && currentOrigin) {
        pushSegment(result, currentText, currentOrigin, false);
      }
      currentOrigin = origin;
      currentText = text[i];
    } else {
      currentText += text[i];
    }
  }

  if (currentText.length > 0 && currentOrigin) {
    pushSegment(result, currentText, currentOrigin, false);
  }
}

/**
 * Processes text from the visible string that is being deleted (DELETE in diff).
 * Determines if it's original text (newDelete) or old insertion (oldInsert + newDelete).
 */
function processVisibleTextDeletion(
  text: string,
  startPos: number,
  positionToOrigin: SegmentOrigin[],
  result: VisualDiffSegment[]
): void {
  let currentOrigin: SegmentOrigin | null = null;
  let currentText = '';

  for (let i = 0; i < text.length; i++) {
    const origin = positionToOrigin[startPos + i];

    if (origin !== currentOrigin) {
      if (currentText.length > 0 && currentOrigin) {
        pushSegment(result, currentText, currentOrigin, true);
      }
      currentOrigin = origin;
      currentText = text[i];
    } else {
      currentText += text[i];
    }
  }

  if (currentText.length > 0 && currentOrigin) {
    pushSegment(result, currentText, currentOrigin, true);
  }
}

/**
 * Pushes a segment with appropriate flags based on origin and whether deleted.
 */
function pushSegment(
  result: VisualDiffSegment[],
  text: string,
  origin: SegmentOrigin,
  isDeleted: boolean
): void {
  // Split by range markers to keep them isolated
  const parts = splitTextByRangeMarkers(text);

  for (const part of parts) {
    if (part.length === 0) continue;

    // Range markers are always new inserts (they come from the final text via EQUAL)
    if (part === RANGE_START || part === RANGE_END) {
      result.push({ newInsert: true, text: part });
      continue;
    }

    if (origin === 'equal') {
      if (isDeleted) {
        result.push({ newDelete: true, text: part });
      } else {
        result.push({ text: part });
      }
    } else if (origin === 'oldInsert') {
      if (isDeleted) {
        result.push({ newDelete: true, oldInsert: true, text: part });
      } else {
        result.push({ oldInsert: true, text: part });
      }
    }
    // Note: oldDelete origin is handled separately via placeholder logic
  }
}

/**
 * Checks if text is a range marker.
 */
function isRangeMarkerText(text: string): boolean {
  return text === RANGE_START || text === RANGE_END;
}

/**
 * Merges adjacent VisualDiffSegment that have the same flags.
 * Range markers are never merged with other text to keep them isolated.
 */
function mergeAdjacentSegments(segments: VisualDiffSegment[]): VisualDiffSegment[] {
  if (segments.length === 0) return segments;

  const result: VisualDiffSegment[] = [];

  for (const segment of segments) {
    const last = result[result.length - 1];

    // Never merge range markers with other text
    if (isRangeMarkerText(segment.text) || (last && isRangeMarkerText(last.text))) {
      result.push({ ...segment });
      continue;
    }

    if (last && hasSameFlags(last, segment)) {
      last.text += segment.text;
    } else {
      result.push({ ...segment });
    }
  }

  return result;
}

/**
 * Checks if two VisualDiffSegment have the same flags.
 */
function hasSameFlags(a: VisualDiffSegment, b: VisualDiffSegment): boolean {
  return (
    !!a.oldInsert === !!b.oldInsert &&
    !!a.oldDelete === !!b.oldDelete &&
    !!a.newInsert === !!b.newInsert &&
    !!a.newDelete === !!b.newDelete
  );
}
